import type { TemplateAttribute } from '@/lib/api/client'
import { flattenToolPages, useListToolsQuery } from '@/services/queries/tools'
import type { BillOfMaterials, ToolsBillOfMaterials } from '@/state'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import commands from '../commands'
import type { CommandItem } from '../commands/types'

const getPartsSignature = (bom: BillOfMaterials) =>
  bom.map((part) => part.partName).join('|')

const getCustomPartsFromTemplateAttributes = (
  templateAttributes: TemplateAttribute[],
): BillOfMaterials => {
  const partsTable = templateAttributes.find(
    (template) =>
      template.data_type === 'table' &&
      template.template_values.type === 'parts-table',
  )
  const rows = (partsTable?.template_values as any)?.rows

  if (!Array.isArray(rows)) {
    return []
  }

  return rows
    .filter((row: any) => typeof row?.partName === 'string')
    .map((row: any) => ({
      partName: row.partName,
      quantity: Number(row.quantity) || 0,
    }))
}

const mergeParts = (...partsLists: BillOfMaterials[]): BillOfMaterials => {
  const seenParts = new Set<string>()

  return partsLists.flatMap((parts) =>
    parts.filter((part) => {
      if (seenParts.has(part.partName)) {
        return false
      }
      seenParts.add(part.partName)
      return true
    }),
  )
}

const getToolsSignature = (tools: ToolsBillOfMaterials) =>
  tools.map((tool) => `${tool.toolId || ''}:${tool.toolName}`).join('|')

const getCurrentOperationTools = ({
  templateAttributes,
  documentTools,
}: {
  templateAttributes: TemplateAttribute[]
  documentTools: ToolsBillOfMaterials
}): ToolsBillOfMaterials => {
  const toolsTable = templateAttributes.find(
    (template) =>
      template.data_type === 'table' &&
      template.template_values.type === 'tools-table',
  )
  const rows = (toolsTable?.template_values as any)?.rows

  if (!Array.isArray(rows)) {
    return []
  }

  return rows.reduce((accum, row: any) => {
    const toolId =
      typeof row?.toolId === 'string' && row.toolId.trim() !== ''
        ? row.toolId
        : undefined
    const documentTool = toolId
      ? documentTools.find((tool) => tool.toolId === toolId)
      : undefined
    const rawToolName = documentTool?.toolName || row?.toolName
    const toolName = typeof rawToolName === 'string' ? rawToolName.trim() : ''

    if (!toolName && !toolId) {
      return accum
    }

    const tool = {
      toolName,
      toolId,
    }
    const alreadyExists = accum.some(
      (existingTool) => existingTool.toolId === tool.toolId,
    )

    if (!alreadyExists) {
      accum.push(tool)
    }

    return accum
  }, [] as ToolsBillOfMaterials)
}

export const useCommandItems = ({
  commandName,
  documentPageId,
  query,
  bom,
  workInstructionsBom,
  fullBom,
  documentTools,
  templateAttributes = [],
}: {
  commandName: string
  documentPageId: string
  query: string
  bom: BillOfMaterials
  workInstructionsBom: BillOfMaterials
  fullBom: BillOfMaterials
  documentTools: ToolsBillOfMaterials
  templateAttributes?: TemplateAttribute[]
}) => {
  const customPartsBom = useMemo(
    () => getCustomPartsFromTemplateAttributes(templateAttributes),
    [templateAttributes],
  )
  const operationBom = useMemo(
    () => mergeParts(bom, customPartsBom),
    [bom, customPartsBom],
  )
  const operationBomSignature = useMemo(
    () => getPartsSignature(operationBom),
    [operationBom],
  )
  const workInstructionsBomSignature = useMemo(
    () => getPartsSignature(workInstructionsBom),
    [workInstructionsBom],
  )
  const fullBomSignature = useMemo(() => getPartsSignature(fullBom), [fullBom])
  const operationTools = useMemo(
    () => getCurrentOperationTools({ templateAttributes, documentTools }),
    [templateAttributes, documentTools],
  )
  const operationToolsSignature = useMemo(
    () => getToolsSignature(operationTools),
    [operationTools],
  )
  const documentToolsSignature = useMemo(
    () => getToolsSignature(documentTools),
    [documentTools],
  )
  const toolsSearchQuery = useMemo(() => {
    /**
     * Slash command queries arrive as `tools:<typed text>`.
     * We strip only the command prefix so backend search follows live typing.
     */
    if (commandName !== 'tools') return ''
    return query.replace(/^\/?tools:/, '').trimStart()
  }, [commandName, query])
  const { data: toolsData, dataUpdatedAt: toolsDataUpdatedAt } =
    useListToolsQuery({
      search: toolsSearchQuery,
      limit: 100,
      offset: 0,
      // Slash search must stay org-wide; do not scope by document version here.
      enabled: commandName === 'tools',
    })
  const tools = useMemo(() => flattenToolPages(toolsData), [toolsData])

  return useQuery({
    staleTime: 0,
    // Include tools query freshness so tools slash results recompute whenever
    // the backing tools list changes (rename/create/delete).
    queryKey: [
      'editorCommands',
      commandName,
      documentPageId,
      operationBomSignature,
      workInstructionsBomSignature,
      fullBomSignature,
      operationToolsSignature,
      documentToolsSignature,
      toolsSearchQuery,
      toolsDataUpdatedAt,
    ],
    queryFn: async (): Promise<CommandItem[]> => {
      const command = commands.find((command) => command.name === commandName)
      if (!command) {
        return []
      }
      return command.getItems({
        documentPageId,
        bom: operationBom,
        workInstructionsBom,
        fullBom,
        operationTools,
        documentTools,
        tools,
      })
    },
  })
}
